import type { LiveInventoryReturnValues } from '@/types/api/live-inventory'

// Base return values that are always included
const baseReturnValues: LiveInventoryReturnValues[] = [
  'accrued_interest',
  'accrued_interest_date',
  'accrued_interest_pop',
  'accretion_compounding_method',
  // 'ai_issuer', // IMPORTANT: AIIssuer is stored in RefData DB but gets mapped to 'issuer' in Live Inventory responses.
  'amount_outstanding',
  'amount_outstanding_date',
  'asks', // Not a ReferenceDataField
  'ask_eval_price_diff',
  'ask_eval_ytw_diff',
  'ask_g_spread',
  'ask_i_spread',
  'ask_oas',
  'ask_price',
  'ask_ytc',
  'ask_ytm',
  'ask_ytw',
  'ask_ytw_spread_bps',
  'ask_z_spread',
  'asset_type',
  'bank_qualified',
  'benchmark_isin',
  'benchmark_yield',
  'best_ask_post_tax_ytw',
  'best_ask_tax_equivalent_yield',
  'bids', // Not a ReferenceDataField
  'bid_eval_price_diff',
  'bid_eval_ytw_diff',
  'bid_g_spread',
  'bid_i_spread',
  'bid_oas',
  'bid_price',
  'bid_ytc',
  'bid_ytm',
  'bid_ytw',
  'bid_ytw_spread_bps',
  'bid_z_spread',
  'bond_form_type',
  'bond_issuer_rv_score',
  'bond_warrant',
  'callable',
  'call_frequency',
  'call_notification_convention',
  'call_notification_max',
  'call_notification_min',
  'call_type',
  'close_price',
  'close_price_asof',
  'close_ytw',
  'coco_event_type',
  'coco_indicator',
  'coco_trigger_action',
  'coco_trigger_level_type',
  'coco_trigger_level_value',
  'coco_trigger_type',
  'contingent_interest_indicator',
  'continuously_callable_flag',
  'convertible',
  'convexity',
  'country_domicile',
  'country_issue',
  'country_of_risk',
  'coupon',
  'coupon_frequency',
  'coupon_inflation_protected',
  'coupon_subtype',
  'coupon_summary',
  'coupon_type',
  'coupon_type_method',
  'covered_bond_collateral',
  'credit_sensitive_indicator',
  'currency',
  'cusip',
  'dated_date',
  'daycount',
  'description',
  'description_short',
  'duration',
  'duration_date',
  'duration_times_spread',
  'effective_call_date',
  'effective_call_price',
  'effective_duration',
  'eval_price',
  'eval_ytm',
  'eval_ytw',
  'expected_spread',
  'fdic_code',
  'fdic_insured',
  'filter_lists',
  'fitch_outlook',
  'fitch_outlook_date',
  'fitch_rating',
  'fitch_rating_date',
  'fitch_underlying_rating',
  'fitch_underlying_rating_date',
  'first_coupon_date',
  'float_benchmark',
  'float_formula',
  'float_formula_reset_date',
  'float_max_rate',
  'float_min_rate',
  'float_reset_frequency',
  'float_spread',
  'gics_sector_1',
  'gics_sector_2',
  'gics_sector_3',
  'gics_sector_4',
  'has_extraordinary_call',
  'ice_sector_1',
  'ice_sector_2',
  'ice_sector_3',
  'ice_sector_4',
  'increment_amount_secondary',
  'instrument_class',
  'insured',
  'is_144a',
  'is_amt_applicable',
  'is_pre_refunded',
  'is_state_tax_applicable',
  'is_structured_product',
  'isin',
  'issue_date',
  'issue_increment',
  'issue_minimum',
  'issue_minimum_denomination',
  'issue_price',
  'issue_size',
  'issuer',
  'issuer_offset',
  'issuer_sector_rv_score',
  'last_coupon_date',
  'liquidity_institutional_aggregate',
  'liquidity_institutional_buy',
  'liquidity_institutional_sell',
  'liquidity_micro_aggregate',
  'liquidity_micro_buy',
  'liquidity_micro_sell',
  'liquidity_retail_aggregate',
  'liquidity_retail_buy',
  'liquidity_retail_sell',
  'lowest_rating',
  'make_whole_benchmark',
  'make_whole_callable',
  'make_whole_end_date',
  'make_whole_spread',
  'market_spread_price',
  'market_spread_ytm',
  'market_spread_ytw',
  'maturity_date',
  'maturity_redemption_price',
  'maturity_type',
  'maturity_year',
  'workout_year',
  'mid_g_spread',
  'mid_i_spread',
  'mid_oas',
  'mid_ytc',
  'mid_ytm',
  'mid_ytw',
  'mid_ytw_spread_bps',
  'mid_z_spread',
  'min_denom_amount_secondary',
  'moodys_rating',
  'moodys_rating_date',
  'moodys_underlying_rating',
  'moodys_underlying_rating_date',
  'moodys_outlook',
  'moodys_outlook_date',
  'municipal_state',
  'municipal_taxable_federal',
  'municipal_type',
  'muni_obligor_state',
  'muni_sector',
  'naics_sector',
  'naics_subsector',
  'next_call_date',
  'next_call_price',
  'next_coupon_date',
  'objective_score',
  'objective_score_decomposition',
  'obligor',
  'odd_first_coupon',
  'odd_last_coupon',
  'original_issue_date',
  'original_issue_price',
  'original_issue_size',
  'par_value',
  'perpetual',
  'principal_factor',
  'private_placement',
  'prospectus_date',
  'purpose',
  'puttable',
  'qcb_indicator',
  'rating_curve_value',
  'reg_s',
  // 'run_id',
  'sector',
  'sector_offset',
  'security_code',
  'seniority',
  'seniority_adjustment',
  'seniority_type',
  'settlement_convention',
  'sinking_fund',
  'sp_creditwatch',
  'sp_creditwatch_date',
  'sp_outlook',
  'sp_outlook_date',
  'sp_rating',
  'sp_rating_date',
  'sp_underlying_rating',
  'sp_underlying_rating_date',
  'status',
  'strip_type',
  'sukuk_contract_type',
  'sukuk_indicator',
  'sukuk_type',
  'survivor_option',
  'ticker',
  'trace_eligible',
  'trading_restrictions_type',
  'treasury_benchmark',
  'treasury_subtype',
  'type',
  'ultimate_borrower',
  'ultimate_parent',
  'updated_at',
  'us_on_the_run_ind',
  'use_of_proceeds',
  'vrdo_indicator',
  'workout_date',
]

// Default export for backward compatibility
const allLiveInventoryReturnValues = baseReturnValues

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default allLiveInventoryReturnValues
